import React, { useState, useEffect } from 'react'
import { useSite } from '../../contexts/SiteContext'

const STORAGE_KEY_DETECTED = 'lang-detected'
const STORAGE_KEY_SHOWN = 'lang-prompt-shown'
const PROMPT_DELAY_MS = 10000

/**
 * LanguagePrompt — Language detection + switch prompt overlay
 *
 * Behavior:
 * 1. On first visit (no sessionStorage flag), auto-redirects user to browser-detected language.
 * 2. After 10s on the page, shows a non-intrusive bottom-center overlay offering to
 *    switch to the other language (once per session).
 * 3. Does NOT show if user was already auto-redirected (they got the right language).
 *
 * Design: bg-color-primary (navy), text-primary-on-dark — slides up from bottom.
 */
export default function LanguagePrompt() {
  const { currentLang, switchLanguage } = useSite()
  const [visible, setVisible] = useState(false)
  const [animatingOut, setAnimatingOut] = useState(false)

  // Step 1: Auto-detect on first visit
  useEffect(() => {
    const alreadyDetected = sessionStorage.getItem(STORAGE_KEY_DETECTED)
    if (alreadyDetected) return

    // Mark as detected so we never auto-redirect again this session
    sessionStorage.setItem(STORAGE_KEY_DETECTED, 'true')

    const browserLang = navigator.language || ''
    const isGerman = browserLang.toLowerCase().startsWith('de')
    const detectedLang = isGerman ? 'de' : 'en'

    if (detectedLang !== currentLang) {
      // Auto-redirect — also mark prompt as shown (no need to prompt after redirect)
      sessionStorage.setItem(STORAGE_KEY_SHOWN, 'true')
      switchLanguage(detectedLang)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Step 2: Show prompt after 10s (once per session, not after auto-redirect)
  useEffect(() => {
    const alreadyShown = sessionStorage.getItem(STORAGE_KEY_SHOWN)
    if (alreadyShown) return

    const timer = setTimeout(() => {
      sessionStorage.setItem(STORAGE_KEY_SHOWN, 'true')
      setVisible(true)
    }, PROMPT_DELAY_MS)

    return () => clearTimeout(timer)
  }, [])

  const dismiss = () => {
    setAnimatingOut(true)
    setTimeout(() => setVisible(false), 500)
  }

  const handleSwitch = () => {
    const targetLang = currentLang === 'de' ? 'en' : 'de'
    dismiss()
    // Small delay so the slide-out animation is visible before navigation
    setTimeout(() => switchLanguage(targetLang), 300)
  }

  if (!visible) return null

  const promptText = currentLang === 'de' ? 'Prefer English?' : 'Lieber auf Deutsch?'
  const switchLabel = currentLang === 'de' ? 'Switch to EN' : 'Auf DE wechseln'

  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] transition-all duration-[500ms] ${
        animatingOut ? 'translate-y-24 opacity-0' : 'translate-y-0 opacity-100'
      }`}
      role="dialog"
      aria-label="Language suggestion"
    >
      <div className="bg-color-primary rounded-2xl px-5 py-3 flex items-center gap-4 shadow-lg">
        <span className="typography-label text-primary-on-dark whitespace-nowrap">{promptText}</span>
        <button
          onClick={handleSwitch}
          className="typography-label bg-color-accent text-primary-on-dark rounded-full px-4 py-1.5 transition-colors duration-[200ms] hover:opacity-90 whitespace-nowrap"
        >
          {switchLabel}
        </button>
        <button
          onClick={dismiss}
          className="text-secondary-on-dark hover:text-primary-on-dark transition-colors duration-[200ms] text-lg leading-none"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  )
}
